import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import { UploadedFile } from 'src/interfaces/FileManager';

import LightboxImageTrigger from 'src/common/components/LightboxImageTrigger';

import CopyFileLinkButton from './CopyFileLinkButton';
import { FileRowActionsMenu } from './fileTableCells';

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

interface FileCardProps {
  file: UploadedFile;
  mode: 'manage' | 'select';
  multiple?: boolean;
  selected?: boolean;
  onSelect: (file: UploadedFile) => void;
  onDelete: (fileId: string) => void;
  onEdit: (file: UploadedFile) => void;
}

const fileCardTitleSx = {
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '0.8125rem',
  lineHeight: 1.35,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const fileCardMetaSx = {
  minWidth: 0,
  overflow: 'hidden',
} as const;

const endpointLabelKey = (uploadEndpoint: UploadedFile['uploadEndpoint']) => {
  switch (uploadEndpoint) {
    case 'image':
      return 'system.fileManager.types.image';
    case 'document':
      return 'system.fileManager.types.document';
    case 'trackMap':
      return 'system.fileManager.types.trackMap';
    default:
      return 'system.fileManager.fileType';
  }
};

const FileCard = ({
  file,
  mode,
  multiple = false,
  selected = false,
  onSelect,
  onDelete,
  onEdit,
}: FileCardProps) => {
  const { t } = useTranslation();
  const isMultiSelect = mode === 'select' && multiple;
  const isSelectMode = mode === 'select';

  const handleSelect = () => onSelect(file);

  const handleDeleteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(file.id);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  const deleteAction = (
    <Tooltip title={t('base.delete')}>
      <IconButton
        size="small"
        color="error"
        aria-label={t('base.delete')}
        onClick={handleDeleteClick}
        sx={{ bgcolor: 'background.paper' }}
      >
        <DeleteForeverIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const copyLinkAction = (
    <CopyFileLinkButton
      fileUrl={file.fileUrl}
      sx={{ bgcolor: 'background.paper' }}
    />
  );

  const cardActions = isSelectMode ? (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {copyLinkAction}
      {deleteAction}
    </Box>
  ) : undefined;

  const getIcon = () => {
    switch (file.uploadEndpoint) {
      case 'image':
        return <ImageIcon sx={{ fontSize: 60 }} />;
      case 'document':
        return <FileIcon sx={{ fontSize: 60 }} />;
      case 'trackMap':
        return <MapIcon sx={{ fontSize: 60 }} />;
      default:
        return <FileIcon sx={{ fontSize: 60 }} />;
    }
  };

  const ticket = (
    <TicketCard
      actions={cardActions}
      eyebrow={t(endpointLabelKey(file.uploadEndpoint))}
      innerSx={fileCardMetaSx}
      media={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 140,
            bgcolor: 'grey.200',
            position: 'relative',
          }}
        >
          {isMultiSelect ? (
            <FormControlLabel
              control={<Checkbox checked={selected} tabIndex={-1} />}
              label=""
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                zIndex: 1,
                m: 0,
                bgcolor: 'background.paper',
                '& .MuiFormControlLabel-label': { display: 'none' },
              }}
            />
          ) : null}
          {file.uploadEndpoint === 'image' ? (
            <LightboxImageTrigger
              src={file.fileUrl}
              alt={file.altText || file.fileName}
              stopPropagation
              sx={{
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            getIcon()
          )}
        </Box>
      }
      badges={[
        {
          label: t(file.isUsed ? 'system.fileManager.used' : 'system.fileManager.unused'),
          highlighted: file.isUsed,
        },
      ]}
      footer={
        mode === 'manage' ? (
          <FileRowActionsMenu file={file} onEdit={onEdit} onDelete={onDelete} />
        ) : undefined
      }
      {...(isSelectMode
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: handleSelect,
            onKeyDown: handleCardKeyDown,
          }
        : {})}
      sx={[
        { minWidth: 0, overflow: 'hidden' },
        isSelectMode
          ? {
              textAlign: 'left',
              cursor: 'pointer',
              width: '100%',
              outline: isMultiSelect && selected ? '2px solid' : undefined,
              outlineColor: isMultiSelect && selected ? 'primary.main' : undefined,
              '&:hover': { bgcolor: 'action.hover' },
            }
          : {},
      ]}
    >
      <Tooltip title={file.fileName} placement="top" enterDelay={400}>
        <Typography variant="body2" component="div" sx={fileCardTitleSx}>
          {file.fileName}
        </Typography>
      </Tooltip>
      <Typography
        variant="caption"
        component="div"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.25, mb: 0.75 }}
      >
        {formatBytes(file.fileSize)}
      </Typography>
    </TicketCard>
  );

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ minWidth: 0 }}>
      {ticket}
    </Grid>
  );
};

export default FileCard;
